import React, { useMemo, useRef } from "react";

import EnterpriseKpisPanel from "../../../../../intelligence-briefing-library/EnterpriseKpisPanel";
import type { HawkeyeMetricsAccountsInput } from "../../../../../intelligence-briefing-library/hooks/useHawkeyeMetrics";
import TempPublicDataPanel from "../../../../TempViewsRendering/views/TempPublicDataPanel";
import { resolveAccountRecordId } from "../../utils/watchListAccounts";
import BirdsEyeOverview from "../BirdsEyeOverview";
import type { BirdsEyeClusterSummary } from "../BirdsEyeOverview";
import BriefingWorkspace, {
  type BriefingParentTab,
  type BriefingWorkspaceTab,
} from "../BriefingWorkspace";
import type { CreateWatchlistModalSuccess } from "../CreateWatchlistModal";
import KpiDataAvailableToggle from "../KpiDataAvailableToggle";

interface MonitoredTabProps {
  briefingAccounts: any[];
  /**
   * Optional broader account set for Bird's Eye metrics/clusters only
   * (e.g. all watchlist accounts). Briefing still uses selectedCustomerIds.
   */
  birdsEyeAccounts?: any[];
  /** Account IDs used for briefing API + matrix scope. Must stay stable while selecting. */
  selectedCustomerIds: string[];
  /** UI-only selection/highlight. Must not change selectedCustomerIds. */
  highlightedAccountIds?: string[];
  onHighlightedAccountIdsChange?: (accountIds: string[]) => void;
  selectedWatchlistId: string | null;
  briefingParentTab?: BriefingParentTab;
  onBriefingParentTabChange?: (tab: BriefingParentTab) => void;
  briefingSubTab?: BriefingWorkspaceTab;
  onBriefingSubTabChange?: (tab: BriefingWorkspaceTab) => void;
  onSaveAsWatchlist?: (accountIds: string[]) => void;
  onSaveAccountsSuccess?: (result: CreateWatchlistModalSuccess) => void;
  onSelectedBirdsEyeAccountIdsChange?: (accountIds: string[]) => void;
  selectedBirdsEyeAccountIds?: string[];
  onBirdsEyeClusterGroupsChange?: (groups: BirdsEyeClusterSummary[]) => void;
  showUnavailableKpiData?: boolean;
  onShowUnavailableKpiDataChange?: (show: boolean) => void;
  kpiDataCounts?: {
    available: number;
    unavailable: number;
    loading?: boolean;
  };
  onKpiDataCountsChange?: (counts: {
    available: number;
    unavailable: number;
    loading: boolean;
  }) => void;
  onAccountsWithoutKpiDataChange?: (accountIds: string[]) => void;
  showUnavailableBriefingData?: boolean;
  onShowUnavailableBriefingDataChange?: (show: boolean) => void;
  briefingDataCounts?: {
    available: number;
    unavailable: number;
    loading?: boolean;
  };
  onBriefingDataCountsChange?: (counts: {
    available: number;
    unavailable: number;
    loading: boolean;
  }) => void;
  onAccountsWithoutBriefingDataChange?: (accountIds: string[]) => void;
}

const MonitoredTab: React.FC<MonitoredTabProps> = ({
  briefingAccounts,
  birdsEyeAccounts,
  selectedCustomerIds,
  highlightedAccountIds = [],
  onHighlightedAccountIdsChange,
  briefingParentTab,
  onBriefingParentTabChange,
  briefingSubTab,
  onBriefingSubTabChange,
  onSaveAsWatchlist,
  onSaveAccountsSuccess,
  onSelectedBirdsEyeAccountIdsChange,
  selectedBirdsEyeAccountIds = [],
  onBirdsEyeClusterGroupsChange,
  showUnavailableKpiData = true,
  onShowUnavailableKpiDataChange,
  kpiDataCounts = { available: 0, unavailable: 0, loading: true },
  onKpiDataCountsChange,
  onAccountsWithoutKpiDataChange,
  showUnavailableBriefingData = true,
  onShowUnavailableBriefingDataChange,
  briefingDataCounts = { available: 0, unavailable: 0, loading: true },
  onBriefingDataCountsChange,
  onAccountsWithoutBriefingDataChange,
}) => {
  const scopedAccounts = useMemo(() => {
    if (selectedCustomerIds.length === 0) return [];
    const idSet = new Set(
      selectedCustomerIds.map(id => String(id).trim()).filter(Boolean)
    );
    return briefingAccounts.filter(account =>
      idSet.has(resolveAccountRecordId(account))
    );
  }, [briefingAccounts, selectedCustomerIds]);

  const birdsEyeSourceAccounts = useMemo(() => {
    if (birdsEyeAccounts && birdsEyeAccounts.length > 0) {
      return birdsEyeAccounts;
    }
    return scopedAccounts;
  }, [birdsEyeAccounts, scopedAccounts]);

  const hawkeyeAccountsKey = useMemo(
    () =>
      JSON.stringify(
        birdsEyeSourceAccounts
          .map(account => resolveAccountRecordId(account))
          .filter(Boolean)
          .sort()
      ),
    [birdsEyeSourceAccounts]
  );

  const hawkeyeAccountsRef = useRef<
    Array<{
      id: string;
      name: string;
      city?: string;
      country?: string;
      region?: string;
      latitude?: number;
      longitude?: number;
      primary_sponsors: string | null;
      primary_sponsors_id: string | null;
      secondary_sponsors: string | null;
      secondary_sponsors_id: string | null;
    }>
  >([]);

  const hawkeyeAccounts = useMemo(() => {
    const next = birdsEyeSourceAccounts.map(account => ({
      id: resolveAccountRecordId(account),
      name: String(account.name || account.account_name || "Unknown Account"),
      city: account.city as string | undefined,
      country: account.country as string | undefined,
      region: account.region as string | undefined,
      latitude: account.latitude as number | undefined,
      longitude: account.longitude as number | undefined,
      primary_sponsors: (account.primary_sponsors as string | null) ?? null,
      primary_sponsors_id:
        (account.primary_sponsors_id as string | null) ?? null,
      secondary_sponsors: (account.secondary_sponsors as string | null) ?? null,
      secondary_sponsors_id:
        (account.secondary_sponsors_id as string | null) ?? null,
    }));

    const prevKey = JSON.stringify(
      hawkeyeAccountsRef.current
        .map(account => account.id)
        .filter(Boolean)
        .sort()
    );
    if (prevKey === hawkeyeAccountsKey) {
      return hawkeyeAccountsRef.current;
    }

    hawkeyeAccountsRef.current = next;
    return next;
  }, [birdsEyeSourceAccounts, hawkeyeAccountsKey]);

  const metricsAccountsInput = useMemo<HawkeyeMetricsAccountsInput>(
    () => ({
      selectedAccounts: hawkeyeAccounts,
      allWatchlistAccounts: hawkeyeAccounts,
      watchlistId: null,
      selectedMeasurementDate: null,
      latestMeasurementDate: null,
    }),
    [hawkeyeAccounts]
  );

  const briefingPanelRef = useRef<HTMLDivElement>(null);

  return (
    <div className="w-full flex-1 min-w-0 h-full">
      <div
        ref={briefingPanelRef}
        className="main-content flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
      >
        <div className="shrink-0 px-3 pt-3 pb-1">
          <h1 className="section-title mb-0">Insights</h1>
        </div>
        <div className="min-h-0 flex-1">
          <BriefingWorkspace
            briefingParentTab={briefingParentTab}
            onBriefingParentTabChange={onBriefingParentTabChange}
            briefingSubTab={briefingSubTab}
            onBriefingSubTabChange={onBriefingSubTabChange}
            headerRight={
              <KpiDataAvailableToggle
                variant="briefing"
                checked={showUnavailableBriefingData}
                onCheckedChange={checked =>
                  onShowUnavailableBriefingDataChange?.(checked)
                }
                unavailableCount={briefingDataCounts.unavailable}
                loading={briefingDataCounts.loading !== false}
              />
            }
            kpiHeaderRight={
              <KpiDataAvailableToggle
                checked={showUnavailableKpiData}
                onCheckedChange={checked =>
                  onShowUnavailableKpiDataChange?.(checked)
                }
                availableCount={kpiDataCounts.available}
                unavailableCount={kpiDataCounts.unavailable}
                loading={kpiDataCounts.loading !== false}
              />
            }
            tempPublicDataContent={
              <TempPublicDataPanel
                accounts={hawkeyeAccounts.map(a => ({
                  id: a.id,
                  name: a.name,
                }))}
                highlightedAccountIds={highlightedAccountIds}
                onHighlightedAccountIdsChange={onHighlightedAccountIdsChange}
                onSaveAsWatchlist={onSaveAsWatchlist}
                selectionPanelRef={briefingPanelRef}
                showUnavailableBriefingData={showUnavailableBriefingData}
                onBriefingDataCountsChange={onBriefingDataCountsChange}
                onAccountsWithoutBriefingDataChange={
                  onAccountsWithoutBriefingDataChange
                }
              />
            }
            businessKpiContent={
              <EnterpriseKpisPanel
                accounts={hawkeyeAccounts}
                highlightedAccountIds={highlightedAccountIds}
                onHighlightedAccountIdsChange={onHighlightedAccountIdsChange}
                onSaveAsWatchlist={onSaveAsWatchlist}
                showUnavailableKpiData={showUnavailableKpiData}
                onKpiDataCountsChange={onKpiDataCountsChange}
                onAccountsWithoutKpiDataChange={onAccountsWithoutKpiDataChange}
                selectionPanelRef={briefingPanelRef}
              />
            }
            birdsEyeContent={
              <BirdsEyeOverview
                metricsAccountsInput={metricsAccountsInput}
                onClusterGroupsChange={onBirdsEyeClusterGroupsChange}
                onSelectedAccountIdsChange={onSelectedBirdsEyeAccountIdsChange}
                selectedAccountIds={selectedBirdsEyeAccountIds}
                onSaveAsWatchlist={onSaveAsWatchlist}
                onSaveAccountsSuccess={onSaveAccountsSuccess}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

export default MonitoredTab;
